import React, { useState, useEffect } from 'react';
import { Collapse } from 'react-bootstrap';
import clinicbookingService from '../../../../../services/clinicbooking.service';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import Loading from '../../../../common/Loading';

function AddVisitReason() {
    const [visitReasons, setVisitReasons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editReasonId, setEditReasonId] = useState(null);
    const [errorMessage, setErrorMessage] = useState('');
    const { clinicId } = useParams();

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        setValue,
    } = useForm();

    useEffect(() => {
        getAllVisitReasons(true);
    }, []);

    const toBoolean = (value) => value === true || value === 'true';

    const getAllVisitReasons = (showLoader = false) => {
        if (showLoader) {
            setIsLoading(true);
        }
        clinicbookingService
            .getAllVisitReasons(clinicId)
            .then((res) => {
                setVisitReasons(res.data || []);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => {
                if (showLoader) {
                    setIsLoading(false);
                }
            });
    };

    const toggleAddForm = () => {
        setShowAddForm(!showAddForm);
        setErrorMessage('');
        if (showAddForm) {
            reset();
        }
    };

    const handleDelete = (visitReason) => {
        if (visitReason.isDefault) {
            setErrorMessage('Default visit reasons cannot be deleted. Deactivate them instead.');
            return;
        }
        setIsDeleting(true);
        setErrorMessage('');
        clinicbookingService
            .deleteVisitReason(visitReason.clinicId, visitReason.id)
            .then(() => {
                getAllVisitReasons();
            })
            .catch((err) => {
                console.log(err);
                setErrorMessage(err?.response?.data?.message || 'Unable to delete visit reason.');
            })
            .finally(() => {
                setIsDeleting(false);
            });
    };

    const onSubmitEdit = (data) => {
        setIsSubmitting(true);
        setErrorMessage('');
        const editData = {
            id: editReasonId,
            clinicId,
            reason: data.reason.trim(),
            isActive: toBoolean(data.isActive),
        };

        clinicbookingService
            .addUpdateVisitReason(clinicId, editData)
            .then(() => {
                getAllVisitReasons();
                setEditReasonId(null);
            })
            .catch((err) => {
                console.log(err);
                setErrorMessage(err?.response?.data?.message || 'Unable to update visit reason.');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const onSubmitNew = (data) => {
        setIsSubmitting(true);
        setErrorMessage('');
        const newData = {
            clinicId,
            reason: data.reason.trim(),
            isActive: toBoolean(data.isActive),
        };

        clinicbookingService
            .addUpdateVisitReason(clinicId, newData)
            .then(() => {
                reset({ reason: '', isActive: 'true' });
                getAllVisitReasons();
                setShowAddForm(false);
            })
            .catch((err) => {
                console.log(err);
                setErrorMessage(err?.response?.data?.message || 'Unable to add visit reason.');
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <>
            <div className="clinic-booking-toolbar">
                <div>
                    Total: <strong>{visitReasons.length}</strong> reason{visitReasons.length !== 1 ? 's' : ''}
                </div>
                <button
                    type="button"
                    className={`btn btn-sm ${showAddForm ? 'btn-outline-danger' : 'btn-outline-primary'}`}
                    onClick={toggleAddForm}
                >
                    <i className={`bi ${showAddForm ? 'bi-x-lg' : 'bi-plus-lg'} me-1`}></i>
                    {showAddForm ? 'Cancel' : 'Add Reason'}
                </button>
            </div>

            {errorMessage && (
                <div className="alert alert-danger py-2" role="alert">
                    {errorMessage}
                </div>
            )}

            <Collapse in={showAddForm}>
                <div className="mb-3">
                    <div className="notifications-panel">
                        <div className="notifications-panel__header">
                            <i className="bi bi-plus-circle"></i>
                            Add New Visit Reason
                        </div>
                        <div className="notifications-panel__body">
                            <form onSubmit={handleSubmit(onSubmitNew)}>
                                <div className="row g-3">
                                    <div className="col-md-8">
                                        <label htmlFor="reason" className="notifications-form-label">
                                            Visit Reason <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            className={`form-control form-control-sm ${errors.reason ? 'is-invalid' : ''}`}
                                            id="reason"
                                            {...register('reason', {
                                                required: 'Reason is required',
                                                minLength: { value: 2, message: 'Reason must be at least 2 characters' },
                                                maxLength: { value: 255, message: 'Reason must be at most 255 characters' },
                                            })}
                                            placeholder="e.g., Contact Lens Fitting"
                                        />
                                        {errors.reason && <div className="invalid-feedback">{errors.reason.message}</div>}
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="isActive" className="notifications-form-label">
                                            Status <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className={`form-select form-select-sm ${errors.isActive ? 'is-invalid' : ''}`}
                                            id="isActive"
                                            defaultValue="true"
                                            {...register('isActive', { required: 'Status is required' })}
                                        >
                                            <option value="true">Active</option>
                                            <option value="false">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="clinic-booking-form-actions">
                                    <button className="btn btn-success btn-sm" type="submit" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                                                Adding…
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-plus-circle me-1"></i>
                                                Add Reason
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : visitReasons.length > 0 ? (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table clinic-booking-table">
                        <thead>
                            <tr>
                                <th>
                                    <i className="bi bi-chat-left-text"></i> Visit Reason
                                </th>
                                <th>
                                    <i className="bi bi-bookmark"></i> Type
                                </th>
                                <th>
                                    <i className="bi bi-toggle-on"></i> Status
                                </th>
                                <th className="text-end">
                                    <i className="bi bi-gear"></i> Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {visitReasons.map((reason) => (
                                <tr key={reason.id} className={`clinic-booking-row ${editReasonId === reason.id ? 'table-active' : ''}`}>
                                    {editReasonId === reason.id ? (
                                        <>
                                            <td>
                                                <input
                                                    type="text"
                                                    className={`form-control form-control-sm ${errors.reason ? 'is-invalid' : ''}`}
                                                    {...register('reason', {
                                                        required: 'Reason is required',
                                                        minLength: { value: 2, message: 'Reason must be at least 2 characters' },
                                                        maxLength: { value: 255, message: 'Reason must be at most 255 characters' },
                                                    })}
                                                    placeholder="e.g., Contact Lens Fitting"
                                                />
                                                {errors.reason && <div className="invalid-feedback">{errors.reason.message}</div>}
                                            </td>
                                            <td>
                                                <span className="text-muted small">{reason.isDefault ? 'Default' : 'Custom'}</span>
                                            </td>
                                            <td>
                                                <select
                                                    className={`form-select form-select-sm ${errors.isActive ? 'is-invalid' : ''}`}
                                                    {...register('isActive', { required: 'Status is required' })}
                                                >
                                                    <option value="true">Active</option>
                                                    <option value="false">Inactive</option>
                                                </select>
                                            </td>
                                            <td className="text-end">
                                                <div className="d-flex justify-content-end gap-1">
                                                    <button className="btn btn-success btn-sm" onClick={handleSubmit(onSubmitEdit)} disabled={isSubmitting} title="Save">
                                                        {isSubmitting ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-check-lg"></i>}
                                                    </button>
                                                    <button type="button" className="btn btn-outline-secondary btn-sm" onClick={() => setEditReasonId(null)} title="Cancel">
                                                        <i className="bi bi-x-lg"></i>
                                                    </button>
                                                </div>
                                            </td>
                                        </>
                                    ) : (
                                        <>
                                            <td>
                                                <span className="clinic-booking-name">{reason.reason}</span>
                                            </td>
                                            <td>
                                                <span className="text-muted small">{reason.isDefault ? 'Default' : 'Custom'}</span>
                                            </td>
                                            <td>
                                                <span className={`clinic-booking-status-badge ${reason.isActive === true ? 'clinic-booking-status-badge--active' : 'clinic-booking-status-badge--inactive'}`}>
                                                    {reason.isActive === true ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="text-end">
                                                <div className="d-flex justify-content-end gap-1">
                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={() => {
                                                            setEditReasonId(reason.id);
                                                            setValue('reason', reason.reason);
                                                            setValue('isActive', reason.isActive.toString());
                                                        }}
                                                        title="Edit"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>
                                                    {!reason.isDefault && (
                                                        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => handleDelete(reason)} disabled={isDeleting} title="Delete">
                                                            {isDeleting ? <span className="spinner-border spinner-border-sm" role="status"></span> : <i className="bi bi-trash"></i>}
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="clinic-booking-empty">
                    <i className="bi bi-chat-left"></i>
                    No visit reasons configured.
                    <div className="mt-3">
                        <button type="button" className="btn btn-outline-primary btn-sm" onClick={toggleAddForm}>
                            <i className="bi bi-plus-lg me-1"></i>
                            Add First Reason
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default AddVisitReason;
